import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { DatePicker as MantineDatePicker } from '@mantine/dates';
import '@mantine/dates/styles.css';
import Input from '@components/common/input';
import { formatDateForMoment, isValidDateFormat } from '@utils/date';
import './styles.module.scss';
import useClickOutside from '@hooks/helpers/useClickOutside';
import { formatDate, getBirthDisabledDates, getDisableFeature, getDisablePast } from '@utils/index';
import { useLocales } from '@hooks/helpers/useLocales';

interface IProps {
    helperText?: string;
    label?: string;
    placeholder?: string;
    full?: boolean;
    error?: boolean;
    readOnly?: boolean;
    value?: Date | null;
    maxDate?: Date;
    minDate?: Date;
    onChange?: (val: Date | null) => void;
    disabled?: boolean;
    isChange?: boolean;
    isBirthday?: boolean;
    isDisablePast?: boolean;
    isDisableFeature?: boolean;
}

const DatePicker: FC<IProps> = ({
    isDisableFeature,
    isDisablePast,
    isBirthday,
    disabled,
    isChange = true,
    helperText,
    label,
    placeholder,
    full,
    error,
    readOnly,
    value,
    onChange,
    maxDate,
    minDate
}) => {
    const { placeholderLocale } = useLocales();
    const [isFirstRender, setIsFirstRender] = useState(true);
    const [textValue, setTextValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isShown, setIsShown] = useState(true);
    const ref = useRef(null);

    useClickOutside({
        ref,
        callback: () => setIsOpen(false)
    });

    useEffect(() => {
        if (value) {
            setTextValue(formatDate(new Date(value), 'MM/DD/YYYY'));
        }
    }, [value]);

    useEffect(() => {
        setIsShown(false);
        setTimeout(() => setIsShown(true), 0);
    }, [value]);

    const onChangeInputValue = (val: string) => {
        if (value) {
            onChange && onChange(null);
        } else {
            if (isValidDateFormat(val)) {
                const formattedDateString = formatDateForMoment(val, true);
                const parsedDate = new Date(formattedDateString);

                // Validate that the date was parsed correctly
                if (isNaN(parsedDate.getTime())) {
                    console.error('Invalid date format');
                    setTextValue(val);
                    return;
                }

                const minDate = calculateMinDate ? new Date(calculateMinDate) : null;
                const maxDate = calculateMaxDate ? new Date(calculateMaxDate) : null;

                const isWithinRange =
                    (!minDate || parsedDate >= minDate) && (!maxDate || parsedDate <= maxDate);

                if (isWithinRange) {
                    onChange && onChange(parsedDate);
                } else {
                    console.error('Date is out of range');
                }
            }
        }
        setTextValue(val);
    };

    const calculateMinDate = useMemo(() => {
        if (minDate) return minDate;
        if (isDisablePast) return getDisablePast();
        return undefined;
    }, [minDate, isDisablePast]);

    const calculateMaxDate = useMemo(() => {
        if (maxDate) return minDate;
        if (isDisableFeature) return getDisableFeature();
        if (isBirthday) return getBirthDisabledDates();
        return undefined;
    }, [maxDate, isDisableFeature, isBirthday]);

    const calculateDefaultValue = useMemo(() => {
        return calculateMaxDate || calculateMinDate;
    }, [calculateMaxDate, calculateMinDate]);

    useEffect(() => {
        if (isFirstRender && !value && isChange) {
            onChange && onChange(calculateDefaultValue || null);
        }
        setIsFirstRender(false);
    }, [calculateDefaultValue]);

    return (
        <div className={`wrapper ${full ? 'full' : ''}`} ref={ref}>
            <Input
                value={textValue}
                onChange={onChangeInputValue}
                rightIcon="calendar"
                full={full}
                readOnly={readOnly}
                helperText={helperText}
                error={error}
                disabled={disabled}
                label={label}
                placeholder={placeholder || placeholderLocale('date_format')}
                onClickIcon={() => setIsOpen((state) => !state)}
                onFocus={() => setIsOpen(true)}
            />
            {isOpen && (
                <div className="datepicker_wrapper">
                    {isShown && (
                        <MantineDatePicker
                            value={value ? new Date(value) : null}
                            onChange={(val) => {
                                if (val) {
                                    onChange && onChange(val);
                                } else {
                                    onChange && onChange(null);
                                }
                                setIsOpen(false);
                            }}
                            className="dropdown"
                            defaultDate={value ? new Date(value) : undefined}
                            maxDate={calculateMaxDate}
                            minDate={calculateMinDate}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default DatePicker;
